<template>
  <div class="row pfol">
    <div class="pfol__header small-12">
      {{ $t('tenantProjects.guideline') }}
    </div>
    <div class="small-12">
      <PostFitOutGuideline
        class="pfol__body"
        :class="{'has-access': curReviewerWithAccess, 'read-only': !curReviewerWithAccess}"
        :guidelines="parsedGuidelines"
        :expand="true"
      >
        <template
          v-if="curReviewerWithAccess"
          #itemAction="slotProps"
        >
          <input
            :id="slotProps.sectionItem.id"
            :key="`${slotProps.sectionItem.id}_${slotProps.sectionItem.checked}`"
            class="hkl-guideline__checkbox"
            type="checkbox"
            :checked="slotProps.sectionItem.checked"
            :disabled="slotProps.sectionItem.disabled"
            @change="event => triggerGuideline(event, slotProps.section, slotProps.sectionItem)"
          >
        </template>
      </PostFitOutGuideline>
    </div>
  </div>
</template>

<script>
import { getGuidelines } from '@/api/settings/request'
import { GUIDELINE_TYPE } from '@/modules/settings/engineers/consts'
import { useStoreGetters } from '@/store'
import EventBus from '@/utils/eventBus'
import { GUIDELINE_STATUS } from '@/utils/locale'
import { computed, getCurrentInstance, onBeforeMount, ref } from '@vue/composition-api'
import _ from 'lodash'
import PostFitOutGuideline from './PostFitOutGuideline'
import { PF_HISTORY_ACTION_TYPE, PF_HISTORY_TYPE, POST_FITOUT_STATUS } from './consts'
import { curReviewerWithAccess, useProjectPostFitOuts } from './hook'
import { buildActionText } from './util'

export default {
  components: {
    PostFitOutGuideline,
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const publishedGuideLines = ref([])
    onBeforeMount(async () => {
      const result = await getGuidelines(GUIDELINE_STATUS.PUBLISHED, GUIDELINE_TYPE.POST_FITOUT_LETTER)
      publishedGuideLines.value = result.guidelines || []
    })
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const { pendingGuidelinesList } = useStoreGetters('postFitoutSubmission', ['pendingGuidelinesList'])

    const projectPostFitOuts = useProjectPostFitOuts()
    const lettersHistories = computed(() => {
      const refHistories = projectPostFitOuts.value.map((item, index) => {
        return item.histories.map(h => {
          return Object.assign({
            letterStatus: item.status,
            letterNumber: index,
          }, h)
        })
      })
      return _.flatten(refHistories)
    })
    const checkedAllHistories = computed(() => {
      return _.orderBy(lettersHistories.value, 'createdAt', 'desc')
        .filter(item => item.type === PF_HISTORY_TYPE.GUIDELINE && item.actionType === PF_HISTORY_ACTION_TYPE.CHECKED)
    })
    const currentLetterCheckedHistories = computed(() => {
      return checkedAllHistories.value
        .filter(h => h.letterStatus === POST_FITOUT_STATUS.UNDER_REVIEW)
    })
    const checkedItemIds = computed(() => {
      return pendingGuidelinesList.value && pendingGuidelinesList.value.length ? _.flatten(pendingGuidelinesList.value.map(guideline => guideline.sectionContent)).map(item => item.itemId) : currentLetterCheckedHistories.value.map(h => h.guideId)
    })
    const sortedGuidelines = computed(() => _.orderBy(publishedGuideLines.value, 'order'))
    const parsedGuidelines = computed(() => {
      return sortedGuidelines.value.map(section => {
        const sortedSectionContent = _.orderBy(section.sectionContent, rec => {
          return Number(rec.order)
        })
        const newSecContent = sortedSectionContent.map(rec => {
          const checkedHistories = checkedAllHistories.value.filter(h => h.guideId === rec.itemId)
          rec.actionTexts = checkedHistories.map(item => {
            const number = item.letterNumber ? item.letterNumber + 1 : ''
            return proxy.$t('tenantProjects.postFitout.selectedTo', [buildActionText(item), number])
          })
          if (curReviewerWithAccess.value) {
            const isChecked = checkedItemIds.value.includes(rec.itemId)
            const shouldDisabled = currentLetterCheckedHistories.value.some(h => h.guideId === rec.itemId && h.createdBy.id !== currentUser.value.id)
            rec.checked = isChecked
            rec.disabled = isChecked && shouldDisabled
          }
          return rec
        })
        return { ...section, sectionContent: newSecContent }
      })
    })
    const triggerGuideline = (event, section, secContentItem) => {
      EventBus.$emit('triggerGuideline', event.target.checked, section, secContentItem)
    }

    return {
      projectPostFitOuts,
      checkedAllHistories,
      currentLetterCheckedHistories,
      curReviewerWithAccess,
      parsedGuidelines,
      triggerGuideline,
    }
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
.hkl-guideline {
  &.has-access {
    ::v-deep .title-container {
      width: calc(100% - 24px);
    }
  }
  &__checkbox {
    margin-left: 10px;
  }
  &.read-only {
    ::v-deep {
      .hkl-guideline__child-box {
        margin-bottom: 0;
      }
      .hkl-guideline__child-contentbox {
        border: 0;
      }
      .title-container {
        margin-bottom: 0;
      }
      .collapse .content.with-border {
        border-width: 1px;
        border-style: solid;
        border-top: 0;
        padding: 10px 0;
        margin-bottom: 0;
      }
    }
  }
}
</style>
